<script setup>
import IconPicker from '@/components/form/IconPicker.vue'
import { ENTITY_NAMES } from '@/lib/entities.js'
import { entityLabel } from '@/lib/labels.js'
import { defineAsyncComponent, ref } from 'vue'
import { useMeta } from '@/stores/meta.js'
import { useSession } from '@/stores/session.js'
import UiField from '@/components/ui/UiField.vue'
import UiNotice from '@/components/ui/UiNotice.vue'
import ConditionBuilder from '@/components/form/ConditionBuilder.vue'
import OwnerPicker from '@/components/form/OwnerPicker.vue'
import MultiSelect from '@/components/form/MultiSelect.vue'
import RichTextEditor from '@/components/form/RichTextEditor.vue'
import RowsEditor from './RowsEditor.vue'
import NumberInput from '@/components/form/NumberInput.vue'
import GrowingText from '@/components/form/GrowingText.vue'
import AiTranslateButton from '@/components/catalogue/AiTranslateButton.vue'
import { aiOn } from '@/lib/aiTranslate.js'

/**
 * A form described by data, for configuration objects: text, numbers, switches, choices,
 * {pl, en} texts, colours, conditions in the rules language, owners, and lists of rows.
 *
 * schema: [{ key, label, type (… icon: a picked icon), options?, required?, help?, columns?, entityKey?, wide?, disabled?, empty? (what no choice means) }]
 */
const props = defineProps({
  schema: { type: Array, required: true },
  modelValue: { type: Object, required: true },
  errors: { type: Object, default: () => ({}) },
  creating: Boolean,
  // {pl, en} fields with "Translate with AI" by the other language's (D-355): the catalogue's pages.
  translate: Boolean,
})
const emit = defineEmits(['update:modelValue'])
const meta = useMeta()
const session = useSession()
// Lazily: the editor itself uses this form.
const DictionaryEditor = defineAsyncComponent(() => import('./DictionaryEditor.vue'))
// "+ New dictionary…" from a field's dictionary list (D-136): made in a panel, then chosen.
const creatingFor = ref(null)
function pickDictionary(f, event) {
  if (event.target.value === '__new__') {
    event.target.value = props.modelValue[f.key] ?? ''
    creatingFor.value = f.key
    return
  }
  set(f.key, event.target.value || null)
}
function dictionaryCreated(saved) {
  set(creatingFor.value, saved.key)
  creatingFor.value = null
}
const set = (key, value) => emit('update:modelValue', { ...props.modelValue, [key]: value })
const setI18n = (key, lang, value) => set(key, { ...(props.modelValue[key] || {}), [lang]: value })
const disabled = (f) => (typeof f.disabled === 'function' ? f.disabled(props.modelValue, props.creating) : f.disabled) || (f.createOnly && !props.creating)
const visible = (f) => (typeof f.visible === 'function' ? f.visible(props.modelValue, props.creating) : true)
const error = (f) => props.errors[f.key] || Object.entries(props.errors).find(([k]) => k.startsWith(`${f.key}.`))?.[1] || ''
// The base language: the first of the offer languages.
const baseLang = () => meta.data?.settings?.offer_languages?.[0] || 'pl'
const translatable = (f, lang) => props.translate && aiOn(meta) && lang !== baseLang() && !disabled(f)
const optionsOf = (f) => (typeof f.options === 'function' ? f.options(props.modelValue) : f.options || [])
</script>

<template>
  <div class="grid gap-x-4 gap-y-3 sm:grid-cols-2">
    <template v-for="f in schema" :key="f.key">
      <div v-if="visible(f)" :class="f.wide || ['info', 'rows', 'component', 'condition', 'textarea', 'richtext', 'i18n', 'i18n-text', 'multi', 'json'].includes(f.type) ? 'sm:col-span-2' : ''">
        <UiNotice v-if="f.type === 'info'" tone="info"><p class="whitespace-pre-line">{{ f.text }}</p></UiNotice>
        <div v-else-if="f.type === 'bool'" class="pt-5">
          <label class="flex items-center gap-2 text-sm">
            <input type="checkbox" class="h-4 w-4 accent-brand-600" :checked="!!modelValue[f.key]" :disabled="disabled(f)" @change="set(f.key, $event.target.checked)" />
            {{ f.label }}
          </label>
          <!-- what ticking it does -->
          <p v-if="f.help" class="mt-1 text-xs text-muted">{{ f.help }}</p>
        </div>
        <UiField v-else :label="f.label" :required="f.required" :error="error(f)" :help="f.help">
          <div v-if="f.type === 'i18n' || f.type === 'i18n-text'" class="grid gap-2 sm:grid-cols-2">
            <div v-for="lang in ['pl', 'en']" :key="lang" class="relative">
              <span class="pointer-events-none absolute left-2 top-2 text-3xs font-semibold uppercase text-faint">{{ lang }}</span>
              <textarea v-if="f.type === 'i18n-text'" class="input pl-8" rows="2" :value="modelValue[f.key]?.[lang] || ''" :disabled="disabled(f)" @input="setI18n(f.key, lang, $event.target.value)"></textarea>
              <!-- a name or a phrase that may run long (long): wrapped, growing -->
              <GrowingText v-else-if="f.long" class="input pl-8" :model-value="modelValue[f.key]?.[lang] || ''" :disabled="disabled(f)" @update:model-value="(v) => setI18n(f.key, lang, v)" />
              <input v-else class="input pl-8" :value="modelValue[f.key]?.[lang] || ''" :disabled="disabled(f)" @input="setI18n(f.key, lang, $event.target.value)" />
              <div v-if="translatable(f, lang) && (modelValue[f.key]?.[baseLang()] || '').trim()" class="mt-0.5 flex justify-end">
                <AiTranslateButton :source="modelValue[f.key][baseLang()]" :from="baseLang()" :to="lang" :filled="!!(modelValue[f.key]?.[lang] || '').trim()" @translated="(v) => setI18n(f.key, lang, v)" />
              </div>
            </div>
          </div>
          <textarea v-else-if="f.type === 'textarea'" class="input" rows="3" :value="modelValue[f.key] ?? ''" :disabled="disabled(f)" @input="set(f.key, $event.target.value)"></textarea>
          <RichTextEditor v-else-if="f.type === 'richtext'" :model-value="modelValue[f.key]" :placeholders="f.placeholders || []" compact @update:model-value="(v) => set(f.key, v)" />
          <IconPicker v-else-if="f.type === 'icon'" :model-value="modelValue[f.key] || null" :disabled="disabled(f)" @update:model-value="(v) => set(f.key, v)" />
          <NumberInput v-else-if="f.type === 'number'" :integer="f.step === 1" :min="f.min ?? null" :max="f.max ?? null" :model-value="modelValue[f.key] ?? null" :disabled="disabled(f)" @update:model-value="(v) => set(f.key, v)" />
          <div v-else-if="f.type === 'color'" class="flex items-center gap-2">
            <input type="color" class="h-9 w-12 cursor-pointer rounded border border-default" :value="modelValue[f.key] || '#2458e6'" :disabled="disabled(f)" @input="set(f.key, $event.target.value)" />
            <input class="input" :value="modelValue[f.key] || ''" placeholder="#2458e6" :disabled="disabled(f)" @change="set(f.key, $event.target.value || null)" />
          </div>
          <select v-else-if="f.type === 'select'" class="input" :value="modelValue[f.key] ?? ''" :disabled="disabled(f)" @change="set(f.key, $event.target.value === '' ? null : $event.target.value)">
            <option v-if="!f.required" value="">{{ f.empty || '—' }}</option>
            <option v-for="o in optionsOf(f)" :key="o.value" :value="o.value">{{ o.label }}</option>
          </select>
          <MultiSelect v-else-if="f.type === 'multi'" :model-value="modelValue[f.key] || []" :options="optionsOf(f)" :disabled="disabled(f)" @update:model-value="(v) => set(f.key, v)" />
          <select v-else-if="f.type === 'entity'" class="input" :value="modelValue[f.key] ?? ''" :disabled="disabled(f)" @change="set(f.key, $event.target.value)">
            <option v-for="e in f.entities || ENTITY_NAMES" :key="e" :value="e">{{ entityLabel(e) }}</option>
          </select>
          <select v-else-if="f.type === 'dictionary'" class="input" :value="modelValue[f.key] ?? ''" :disabled="disabled(f)" :data-testid="`schema-dictionary-${f.key}`" @change="pickDictionary(f, $event)">
            <option value="">—</option>
            <option v-for="(d, key) in meta.data?.dictionaries || {}" :key="key" :value="key">{{ meta.localized(d.label) }}</option>
            <option v-if="session.has('dictionary:manage')" value="__new__">{{ $t('dictionaries.newOption') }}</option>
          </select>
          <ConditionBuilder v-else-if="f.type === 'condition'" :entity="modelValue[f.entityKey || 'entity'] || f.entity || 'account'" :model-value="modelValue[f.key] || null" :disabled="disabled(f)" @update:model-value="(v) => set(f.key, v)" />
          <OwnerPicker v-else-if="f.type === 'owner'" :model-value="modelValue[f.key] || null" :users-only="f.usersOnly" :disabled="disabled(f)" @update:model-value="(v) => set(f.key, v)" />
          <component :is="f.component" v-else-if="f.type === 'component'" :model-value="modelValue[f.key]" :form="modelValue" :disabled="disabled(f)" @update:model-value="(v) => set(f.key, v)" />
          <RowsEditor v-else-if="f.type === 'rows'" :columns="f.columns" :model-value="modelValue[f.key] || []" :disabled="disabled(f)" :new-row="f.newRow" :context="modelValue" @update:model-value="(v) => set(f.key, v)" />
          <textarea v-else-if="f.type === 'json'" class="input font-mono text-xs" rows="4" :value="modelValue[f.key] ? JSON.stringify(modelValue[f.key], null, 2) : ''" :disabled="disabled(f)" @change="set(f.key, $event.target.value ? JSON.parse($event.target.value) : null)"></textarea>
          <GrowingText v-else-if="f.long && (!f.type || f.type === 'text')" class="input" :model-value="modelValue[f.key] ?? ''" :disabled="disabled(f)" :placeholder="f.placeholder" @update:model-value="(v) => set(f.key, v)" />
          <input v-else class="input" :type="f.type === 'url' ? 'url' : 'text'" :maxlength="f.max" :value="modelValue[f.key] ?? ''" :disabled="disabled(f)" :placeholder="f.placeholder" @input="set(f.key, $event.target.value)" />
        </UiField>
      </div>
    </template>
    <DictionaryEditor :open="!!creatingFor" @close="creatingFor = null" @saved="dictionaryCreated" />
  </div>
</template>
